import {
  ApiResponse,
  Appointment,
  Client,
  ClientApiDto,
  ClientCreateData,
  ClientSearchParams,
  ClientUpdateData,
  PaginatedResponse,
} from "../../types";
import { Attendee } from "../../types/attendee";
import { formatLocalISOString } from "../../utils/DateTime.utils";
import { apiUrl } from "../../utils/url";
import { getErrorMessage } from "../errors";
import { HttpClient, HttpError } from "../HttpClient";
import { IClientsService } from "./IClientsService";
import { TelephoneData } from "./types";

type ClientPayloadInput = {
  name?: string;
  birthdate?: string;
  cellphone?: string;
  taxNumber?: number | null;
  email?: string;
  address?: string;
  zipCode?: string;
  region?: string;
  loyaltyCard?: string;
  countryId?: number;
  genderId?: number;
  privacyPolicyAccepted?: boolean;
  gdpr?: boolean;
  isActive?: boolean;
  storeId?: number;
  mobileFirstLogin?: boolean;
};

function trimOrEmpty(value: string | undefined): string {
  const t = value?.trim();
  return t ? t : "";
}

function int32OrNull(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

function int64OrNull(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

function buildClientApiDto(input: ClientPayloadInput): ClientApiDto {
  const storeId = input.storeId;
  if (storeId == null || storeId === 0 || !Number.isFinite(storeId)) {
    throw new Error(
      "Loja não selecionada. Selecione uma loja antes de continuar.",
    );
  }

  const taxNumber = int32OrNull(input.taxNumber);
  const birthdateRaw = input.birthdate?.trim() ?? "";

  return {
    name: input.name?.trim() ?? "",
    birthdate: birthdateRaw,
    countryId: int64OrNull(input.countryId),
    genderId: int64OrNull(input.genderId),
    cellphone: input.cellphone?.trim() ?? "",
    taxNumber,
    email: trimOrEmpty(input.email),
    address: trimOrEmpty(input.address),
    zipCode: trimOrEmpty(input.zipCode),
    region: trimOrEmpty(input.region),
    loyaltyCard: trimOrEmpty(input.loyaltyCard),
    privacyPolicyAccepted: input.privacyPolicyAccepted ?? false,
    storeId,
    gdpr: input.gdpr ?? false,
    mobileFirstLogin: input.mobileFirstLogin ?? false,
    isActive: input.isActive ?? true,
  };
}

export class ValidationError extends Error {
  fieldErrors: Record<string, string>;

  constructor(message: string, fieldErrors: Record<string, string>) {
    super(message);
    this.name = "ValidationError";
    this.fieldErrors = fieldErrors;
  }
}

export class ClientsService implements IClientsService {
  private httpClient: HttpClient;

  constructor() {
    // Initialize HTTP client with configuration
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  private normalizeClient(client: Client): Client {
    if (client.storeId == null && client.store?.id != null) {
      return { ...client, storeId: client.store.id };
    }
    return client;
  }

  private normalizePaginatedClients(
    response: PaginatedResponse<Client>,
  ): PaginatedResponse<Client> {
    return {
      ...response,
      content: response.content.map(c => this.normalizeClient(c)),
    };
  }

  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item !== undefined && item !== null && item !== "") {
              searchParams.append(key, item.toString());
            }
          });
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    return searchParams.toString();
  }

  private handleClientError(error: unknown, defaultMessage: string): never {
    if (error instanceof HttpError) {
      if (error.responseData) {
        const errorData = error.responseData;

        if (errorData.errorCode) {
          const errorMessage = getErrorMessage(error, defaultMessage);
          throw new Error(errorMessage);
        }

        if (
          errorData.error === "MethodArgumentNotValidException" &&
          errorData.details
        ) {
          const fieldErrors: Record<string, string> = {};

          if (Array.isArray(errorData.details)) {
            errorData.details.forEach((detail: string) => {
              const match = detail.match(/^(\w+):\s*(.+)$/);
              if (match) {
                const [, fieldName, errorMessage] = match;
                let translatedMessage = errorMessage;
                if (
                  fieldName === "loyaltyCard" &&
                  errorMessage === "Invalid Loyalty Card"
                ) {
                  translatedMessage = "Número de cartão inválido";
                }
                fieldErrors[fieldName] = translatedMessage;
              }
            });
          }

          throw new ValidationError(
            errorData.message || "Validation Failed",
            fieldErrors,
          );
        }

        // Handle DataIntegrityViolationException for duplicate phone numbers
        if (
          errorData.error === "DataIntegrityViolationException" &&
          errorData.message?.includes("client_cellphone_key")
        ) {
          throw new ValidationError(errorData.message || "Validation Failed", {
            cellphone:
              "Número de telemóvel associado a um cliente já existente",
          });
        }
      }

      // Fallback to parsing responseText if responseData is not available
      if (error.responseText && error.responseText.trim()) {
        try {
          const errorData = JSON.parse(error.responseText);

          // Check for error code first
          if (errorData.errorCode) {
            const errorMessage = getErrorMessage(error, defaultMessage);
            throw new Error(errorMessage);
          }

          // Handle DataIntegrityViolationException for duplicate phone numbers
          if (
            errorData.error === "DataIntegrityViolationException" &&
            errorData.message?.includes("client_cellphone_key")
          ) {
            throw new ValidationError(
              errorData.message || "Validation Failed",
              {
                cellphone:
                  "Número de telemóvel associado a um cliente já existente",
              },
            );
          }
          if (
            errorData.error === "MethodArgumentNotValidException" &&
            errorData.details
          ) {
            const fieldErrors: Record<string, string> = {};
            if (Array.isArray(errorData.details)) {
              errorData.details.forEach((detail: string) => {
                const match = detail.match(/^(\w+):\s*(.+)$/);
                if (match) {
                  const [, fieldName, errorMessage] = match;
                  // Translate error messages to Portuguese
                  let translatedMessage = errorMessage;
                  if (
                    fieldName === "loyaltyCard" &&
                    errorMessage === "Invalid Loyalty Card"
                  ) {
                    translatedMessage = "Número de cartão inválido";
                  }
                  fieldErrors[fieldName] = translatedMessage;
                }
              });
            }
            throw new ValidationError(
              errorData.message || "Validation Failed",
              fieldErrors,
            );
          }
        } catch (parseError) {
          if (parseError instanceof Error) {
            throw parseError;
          }
          const errorMessage = getErrorMessage(error, defaultMessage);
          throw new Error(errorMessage);
        }
      }
    }
    const errorMessage = getErrorMessage(error, defaultMessage);
    throw new Error(errorMessage);
  }

  async getClients(
    params: ClientSearchParams,
  ): Promise<PaginatedResponse<Client>> {
    const queryParams: Record<string, unknown> = {
      page: params.page,
      size: params.size,
    };

    if (params.query) {
      queryParams.query = params.query;
    }

    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }

    const queryString = this.buildQueryString(queryParams);
    const endpoint = `/clients${queryString ? `?${queryString}` : ""}`;

    try {
      const response =
        await this.httpClient.get<PaginatedResponse<Client>>(endpoint);
      return this.normalizePaginatedClients(response);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível pesquisar clientes. Ajuste os termos de pesquisa e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getClientsCount(storeId?: number): Promise<number> {
    try {
      const endpoint = storeId
        ? `/clients/count?storeId=${storeId}`
        : "/clients/count";
      return await this.httpClient.get<number>(endpoint);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar a contagem de clientes. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async searchClientByPhone(phone: string): Promise<Client> {
    const endpoint = `/clients/cellPhone/${encodeURIComponent(phone)}`;

    try {
      const client = await this.httpClient.get<Client>(endpoint);
      return this.normalizeClient(client);
    } catch (error) {
      if (
        error instanceof HttpError &&
        error.responseData?.errorCode === "ENTITY_NOT_FOUND"
      ) {
        throw new Error(
          "Cliente não encontrado. Confirme o número de telefone ou crie um novo cliente.",
        );
      }

      const errorMessage = getErrorMessage(
        error,
        "Não foi possível encontrar o cliente. Confirme o número de telefone ou crie um novo cliente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getTelephoneDataByCellPhone(phone: string): Promise<TelephoneData> {
    const endpoint = `/clients/cellPhone/${encodeURIComponent(phone)}`;

    try {
      return await this.httpClient.get<TelephoneData>(endpoint);
    } catch (error) {
      if (
        error instanceof HttpError &&
        error.responseData?.errorCode === "ENTITY_NOT_FOUND"
      ) {
        throw new Error(
          "Cliente não encontrado. Confirme o número de telefone ou crie um novo cliente.",
        );
      }

      const errorMessage = getErrorMessage(
        error,
        "Não foi possível encontrar o cliente. Confirme o número de telefone ou crie um novo cliente.",
      );
      throw new Error(errorMessage);
    }
  }

  async searchClientByPhoneOrName(
    clientSearch: string,
    attendeeSearch: string,
    page: number,
    size: number,
    sort: string[] = ["name"],
  ): Promise<PaginatedResponse<Client>> {
    const queryParams: Record<string, unknown> = {
      page,
      size,
    };

    if (clientSearch) {
      queryParams.clientSearch = clientSearch;
    }

    if (attendeeSearch) {
      queryParams.attendeeSearch = attendeeSearch;
    }

    if (sort && sort.length > 0) {
      queryParams.sort = sort;
    }

    const queryString = this.buildQueryString(queryParams);
    const endpoint = `/clients/search?${queryString}`;

    try {
      const response =
        await this.httpClient.get<PaginatedResponse<Client>>(endpoint);
      return this.normalizePaginatedClients(response);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível pesquisar clientes. Ajuste os termos de pesquisa e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getClientById(id: string): Promise<ApiResponse<Client>> {
    try {
      const client = await this.httpClient.get<Client>(`/clients/${id}`);
      return {
        success: true,
        content: this.normalizeClient(client),
        message: "Client fetched successfully",
        timestamp: formatLocalISOString(new Date()),
      };
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os dados do cliente. Confirme se o cliente ainda existe e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async createClient(
    clientData: ClientCreateData,
  ): Promise<ApiResponse<number>> {
    try {
      const payload = buildClientApiDto(clientData);
      const clientId = await this.httpClient.post<number>(
        "/clients",
        payload,
        {},
        true,
      );
      return {
        success: true,
        content: clientId,
        message: "Client created successfully",
        timestamp: formatLocalISOString(new Date()),
      };
    } catch (error) {
      this.handleClientError(
        error,
        "Não foi possível criar o cliente. Confirme os campos obrigatórios e tente novamente.",
      );
    }
  }

  async updateClient(
    id: string,
    clientData: ClientUpdateData,
  ): Promise<ApiResponse<void>> {
    try {
      const payload = buildClientApiDto(clientData);
      await this.httpClient.put(`/clients/${id}`, payload);
      return {
        success: true,
        content: undefined,
        message: "Client updated successfully",
        timestamp: formatLocalISOString(new Date()),
      };
    } catch (error) {
      this.handleClientError(
        error,
        "Não foi possível atualizar o cliente. Confirme os campos e a loja associada.",
      );
    }
  }

  async deleteClient(id: string): Promise<ApiResponse<void>> {
    try {
      return await this.httpClient.delete<ApiResponse<void>>(`/clients/${id}`);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível eliminar o cliente. Confirme se o cliente ainda existe e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getClientPets(clientId: string): Promise<Attendee[]> {
    try {
      return await this.httpClient.get<Attendee[]>(
        `/attendees/client/${clientId}`,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os animais do cliente. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }

  async getClientAppointments(
    clientId: string,
    page: number,
    size: number,
  ): Promise<PaginatedResponse<Appointment>> {
    try {
      return await this.httpClient.get<PaginatedResponse<Appointment>>(
        `/clients/${clientId}/appointments?page=${page}&size=${size}`,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível carregar os agendamentos do cliente. Atualize a página e tente novamente.",
      );
      throw new Error(errorMessage);
    }
  }
}
